<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, provide } from "vue";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import { randomId } from "@/shared/utils/helpers";

const props = defineProps({
    block: {
        type: Object,
        required: true,
    },
    pageColors: {
        type: Object,
        required: true,
    },
    pageColorsStyles: {
        type: Object,
        required: true,
    },
});

provide("isMinimap", false);
provide("isPreview", false);

const previewRef = ref<HTMLElement | null>(null);
const containerRef = ref<HTMLElement | null>(null);
const scale = ref<number | null>(null);

function calculateScale() {
    const previewEl = previewRef.value;
    const containerEl = containerRef.value;

    if (!previewEl || !containerEl) return;

    const previewWidth = 271;
    const desktopWidth = 1800;
    const newScale = previewWidth / desktopWidth;
    scale.value = newScale;

    setTimeout(() => {
        const actualHeight = containerEl.scrollHeight;
        if (actualHeight > 0) {
            previewEl.style.height = `${actualHeight * (scale.value ?? 1)}px`;
        }
    });
}

function getPreviewProps() {
    const id = randomId();
    const webId = id.toString(24);

    return {
        block: {
            id,
            webId,
            dataValues: props.block.dataFields.reduce(
                (acc: Record<string, unknown>, field: any) => {
                    acc[field.value] = field.defaultData;
                    return acc;
                },
                {}
            ),
            styleValues: props.block.styleFields.reduce(
                (acc: Record<string, unknown>, field: any) => {
                    acc[field.value] = field.default;
                    return acc;
                },
                {}
            ),
        } satisfies PageBlock<any>,
        pageColors: props.pageColors,
        isPreview: false,
    };
}

onMounted(() => {
    calculateScale();
    window.addEventListener("resize", calculateScale);
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", calculateScale);
});
</script>

<template>
    <div ref="previewRef" class="w-full overflow-hidden border border-gray-300 rounded-xl relative">
        <div
            ref="containerRef"
            class="w-[1800px] will-change-transform"
            :style="{
                transform: scale ? `scale(${scale})` : undefined,
                transformOrigin: 'top left',
                ...pageColorsStyles,
            }"
        >
            <component
                :is="block.component"
                v-bind="getPreviewProps()"
                class="pointer-events-none"
            />
        </div>
    </div>
</template>
